import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Box,
  Card,
  CardContent,
  Chip,
  LinearProgress
} from '@mui/material';
import {
  AddCircleOutline,
  Description,
  Assignment,
  Schedule,
  Psychology,
  CheckCircle,
  TrendingUp,
  Insights
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const DashboardPage: React.FC = () => {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();

  // Mock data - will be replaced with real data from API
  const activeAgreements = 1;
  const progressPercentage = 45;
  const upcomingTasks = 3;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Welcome back, {user?.firstName}!
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Your Trust Implementation Platform - Track, learn, and execute your journey
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Quick Stats */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Active Agreements
                  </Typography>
                  <Typography variant="h4">{activeAgreements}</Typography>
                </Box>
                <Description color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Progress
                  </Typography>
                  <Typography variant="h4">{progressPercentage}%</Typography>
                </Box>
                <Schedule color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Upcoming Tasks
                  </Typography>
                  <Typography variant="h4">{upcomingTasks}</Typography>
                </Box>
                <Assignment color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Documents
                  </Typography>
                  <Typography variant="h4">12</Typography>
                </Box>
                <Description color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Active Agreement Card */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Current Trust Launch Journey</Typography>
              <Chip label="IN PROGRESS" color="primary" />
            </Box>
            <Typography variant="subtitle1" gutterBottom>
              Agreement #AGR-2025-001
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              53 components selected • 90-day implementation plan
            </Typography>
            <Box sx={{ my: 2 }}>
              <Typography variant="body2" gutterBottom>Overall Progress</Typography>
              <LinearProgress variant="determinate" value={progressPercentage} sx={{ height: 10, borderRadius: 5 }} />
            </Box>
            <Box display="flex" gap={2} mt={2}>
              <Button
                variant="contained"
                onClick={() => navigate('/agreements/1/timeline')}
              >
                View Timeline
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/agreements/1')}
              >
                View Details
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Quick Actions
            </Typography>
            <Box display="flex" flexDirection="column" gap={2}>
              <Button
                variant="contained"
                startIcon={<AddCircleOutline />}
                fullWidth
                onClick={() => navigate('/create-agreement')}
              >
                Start New Journey
              </Button>
              <Button
                variant="outlined"
                startIcon={<Assignment />}
                fullWidth
                onClick={() => navigate('/dashboard')}
              >
                My Tasks
              </Button>
              <Button
                variant="outlined"
                startIcon={<Description />}
                fullWidth
                onClick={() => navigate('/dashboard')}
              >
                View Documents
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Implementation Science Metrics */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Psychology color="primary" />
              <Typography variant="h6">
                Implementation Science Insights
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" paragraph>
              Evidence-based tracking of your implementation journey across capability, opportunity, and motivation.
            </Typography>
            <Grid container spacing={2} mt={1}>
              <Grid item xs={12} sm={4}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <TrendingUp color="primary" />
                      <Typography variant="subtitle2">Capability Score</Typography>
                    </Box>
                    <Typography variant="h4" color="primary">87%</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Knowledge & understanding
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <CheckCircle color="success" />
                      <Typography variant="subtitle2">Fidelity Rate</Typography>
                    </Box>
                    <Typography variant="h4" color="success.main">92%</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Plan adherence
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Insights color="warning" />
                      <Typography variant="subtitle2">Implementation Stage</Typography>
                    </Box>
                    <Typography variant="h6">Month 2</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Active implementation
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Learning Resources (LMS Focus) */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              🎓 Learning Resources & Community
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Explore trust management resources, engage with the community, and learn from other participants' journeys.
            </Typography>
            <Grid container spacing={2} mt={1}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ cursor: 'pointer', '&:hover': { boxShadow: 4 } }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      📚 Component Library
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Browse all 53 trust components with detailed explanations
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ cursor: 'pointer', '&:hover': { boxShadow: 4 } }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      💬 Community Forum
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Ask questions and share experiences with others
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ cursor: 'pointer', '&:hover': { boxShadow: 4 } }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      🎯 Success Stories
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Learn from completed trust launch journeys
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ cursor: 'pointer', '&:hover': { boxShadow: 4 } }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      📖 Guides & Tutorials
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Step-by-step guides for each stage
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default DashboardPage;

